import * as React from "react";
import { useMinimumWageQuery } from "../useMinimumWageQuery";
import { MinimalWagePageProps } from "../types";
import { useMinimumWageMutation } from "../useMinimumWageMutation";
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext";
import { MinimumWageCountryLayout } from "../components/MinimumWageCountryLayout";
import { FieldName, MinimumWageOptionsSection } from "../containers/MinimumWageOptionsSection";
import { MinimumWageAllowanceExpandableItemSection } from "../containers/MinimumWageAllowanceExpandableItemSection";
import { CalculationStartDateSection } from "../containers/CalculationStartDateSection";
import { MinimumWagePolishOptionsSection } from "../containers/MinimumWagePolishOptionsSection";
import { GenericMinimumWageExpandableItemSection } from "../containers/GenericMinimumWageExpandableItemSection";
import { GermanyMinimumWageConfigurationModel } from "@/app/models/configuration/minimumWage/germanyMinimumWageConfigurationModel";
import { MinimumWageAccomodationExpandableItemSection } from "../containers/MinimumWageAccomodationExpandableItemSection";

import { MinimalCountryAccomodationType } from "@/app/constants/configuration/minimalCountryAccomodationType";
import { PreLoader } from "../components/PreLoader"
import { buildMinimumWageDiffRenderers } from '../minimumWageDiffUtils';

const defaultFields: FieldName[] = [
    "printCalculatedHours",
    "calculateCabotageAndCrossTrade",
]

const accomodationTypes = [
    MinimalCountryAccomodationType.Day,
    MinimalCountryAccomodationType.Month,
]

const allowedCurrenciesInner = [
    'EUR',
]

const allowedCurrencies = [
    'EUR', 'PLN'
]

const translateKey = 'germanyConfiguration'

const country = 'germany'

export const GermanyMinimumWageSettingsPage = ({ match }: MinimalWagePageProps) => {
    const { companyId } = match.params
    const q = useMinimumWageQuery(companyId)
    const up = useMinimumWageMutation(companyId)
    const { t } = useLocalize()

    const [state, setState] = React.useState<GermanyMinimumWageConfigurationModel | null>(null)

    React.useEffect(() => {
        setState(null)
    }, [companyId])

    React.useEffect(() => {
        if (q.data) {
            const newState = structuredClone(q.data.germanyConfiguration)
            setState(newState)
        }
    }, [q.data])

    const diffRenderers = React.useMemo(() => buildMinimumWageDiffRenderers('germanyConfiguration', t), [t])
    const diffLabels = React.useMemo<Record<string, string>>(() => ({
        configuration: t('configuration.minimumWages.options'),
        expandableItems: t('configuration.minimumWages.minimalHourRateTitle'),
        allowanceExpandableItems: t('configuration.minimumWages.minimalAllowanceTitle'),
        accomodationExpandableItems: t('configuration.minimumWages.minimalAccommodationAdditionTitle'),
    }), [t])

    if (!state || !q.data) {
        return <PreLoader />
    }

    const isDirty = JSON.stringify(state) !== JSON.stringify(q.data.germanyConfiguration)

    return (
        <MinimumWageCountryLayout
            selectedCountryValue={country}
            isDirty={isDirty}
            handleSave={() => up.mutateAsync({ ...q.data, germanyConfiguration: state })}
            handleCancel={() => setState(q.data.germanyConfiguration)}
            dataTestId="germany-minimum-wage-settings-page"
            isLoading={(q.isFetching || up.isPending)}
            diffBefore={q.data.germanyConfiguration as Record<string, unknown>}
            diffAfter={state as Record<string, unknown>}
            diffLabels={diffLabels}
            diffRenderers={diffRenderers}
        >
            <CalculationStartDateSection
                state={state}
                setState={setState}
            />

            <div className={!state.configuration.calculateAmountsForCountry ? 'tw-opacity-50 tw-pointer-events-none' : ''}>
                    <GenericMinimumWageExpandableItemSection
                        allowedCurrencies={allowedCurrenciesInner}
                        state={state}
                        setState={setState}
                    />

                    <MinimumWageAllowanceExpandableItemSection 
                        accomodationTypes={accomodationTypes}
                        title={t('configuration.minimumWages.germanyConfiguration.minimalAllowanceTitle')}
                        state={state}
                        setState={setState}
                        readOnly
                    />

                    <MinimumWageAccomodationExpandableItemSection 
                        title={t('configuration.minimumWages.germanyConfiguration.minimalAccommodationAdditionTitle')}
                        state={state}
                        setState={setState}
                        readOnly
                    />

                    <MinimumWageOptionsSection
                        className='tw-my-5'
                        loading={false}
                        fields={defaultFields}
                        translateKey={translateKey}
                        state={state}
                        accommodationAndAllowanceMaxDate={q.data.generalConfiguration.accommodationAndAllowanceMaxDate}
                        setState={setState}
                    />

                    <MinimumWagePolishOptionsSection 
                        className='tw-my-5'
                        loading={false}
                        state={state}
                        setState={setState}
                    />
            </div>

            <div style={{ minHeight: '90px' }} />
        </MinimumWageCountryLayout>
    )
}
